import { z } from 'zod';

import { CashLocationTypeSchema } from './cash-locations.schema.js';
import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import {
  type CashDenominationInventoryCheckpointApplicationStatus,
  type CashDenominationInventoryCheckpointDto,
  type CashDenominationInventoryCheckpointInventoryStatus,
  type CashDenominationInventoryCheckpointOrigin,
  type CashDenominationInventoryCheckpointReadStatus,
  type CashDenominationInventoryCheckpointSource,
  type CashDenominationInventoryCheckpointSourceEventSide,
  type CashDenominationInventoryCheckpointSourceEventType,
  type CashDenominationInventoryCheckpointStatusReason,
  type CashDenominationInventoryReadDto,
  type GetCashDenominationInventoryCheckpointRequest,
  type GetCashDenominationInventoryCheckpointResponse,
  cashDenominationInventoryCheckpointApplicationStatusValues,
  cashDenominationInventoryCheckpointInventoryStatusValues,
  cashDenominationInventoryCheckpointOriginValues,
  cashDenominationInventoryCheckpointReadStatusValues,
  cashDenominationInventoryCheckpointSourceEventSideValues,
  cashDenominationInventoryCheckpointSourceEventTypeValues,
  cashDenominationInventoryCheckpointSourceValues,
  cashDenominationInventoryCheckpointStatusReasonValues,
} from './cash-denomination-inventory-checkpoints.dto.js';

const timestampSchema = z.iso.datetime();

export const CashDenominationInventoryCheckpointInventoryStatusSchema: z.ZodType<CashDenominationInventoryCheckpointInventoryStatus> =
  z.enum(cashDenominationInventoryCheckpointInventoryStatusValues);

export const CashDenominationInventoryCheckpointApplicationStatusSchema: z.ZodType<CashDenominationInventoryCheckpointApplicationStatus> =
  z.enum(cashDenominationInventoryCheckpointApplicationStatusValues);

export const CashDenominationInventoryCheckpointOriginSchema: z.ZodType<CashDenominationInventoryCheckpointOrigin> =
  z.enum(cashDenominationInventoryCheckpointOriginValues);

export const CashDenominationInventoryCheckpointSourceSchema: z.ZodType<CashDenominationInventoryCheckpointSource> =
  z.enum(cashDenominationInventoryCheckpointSourceValues);

export const CashDenominationInventoryCheckpointSourceEventTypeSchema: z.ZodType<CashDenominationInventoryCheckpointSourceEventType> =
  z.enum(cashDenominationInventoryCheckpointSourceEventTypeValues);

export const CashDenominationInventoryCheckpointSourceEventSideSchema: z.ZodType<CashDenominationInventoryCheckpointSourceEventSide> =
  z.enum(cashDenominationInventoryCheckpointSourceEventSideValues);

export const CashDenominationInventoryCheckpointReadStatusSchema: z.ZodType<CashDenominationInventoryCheckpointReadStatus> =
  z.enum(cashDenominationInventoryCheckpointReadStatusValues);

export const CashDenominationInventoryCheckpointStatusReasonSchema: z.ZodType<CashDenominationInventoryCheckpointStatusReason> =
  z.enum(cashDenominationInventoryCheckpointStatusReasonValues);

export const CashDenominationInventoryCheckpointDtoSchema: z.ZodType<CashDenominationInventoryCheckpointDto> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid().nullable(),
      locationId: z.uuid().nullable(),
      stationId: z.uuid().nullable(),
      cashLocationId: z.uuid(),
      cashLocationType: CashLocationTypeSchema,
      inventoryStatus: CashDenominationInventoryCheckpointInventoryStatusSchema,
      applicationStatus: CashDenominationInventoryCheckpointApplicationStatusSchema,
      origin: CashDenominationInventoryCheckpointOriginSchema,
      source: CashDenominationInventoryCheckpointSourceSchema,
      sourceAuthorityRank: z.number().int().nonnegative(),
      sourceEventType: CashDenominationInventoryCheckpointSourceEventTypeSchema,
      sourceEventId: z.string().trim().min(1).max(200),
      sourceEventSide: CashDenominationInventoryCheckpointSourceEventSideSchema,
      sourceEventOccurredAt: timestampSchema,
      asOf: timestampSchema,
      publishedAt: timestampSchema,
      lastSyncedAt: timestampSchema.nullable(),
      rows: CashDenominationRowsSchema.nullable(),
      totalMinor: z.number().int().nonnegative().nullable(),
      ledgerBalanceMinor: z.number().int(),
      ledgerMovementCount: z.number().int().nonnegative().nullable(),
      ledgerLastMovementAt: timestampSchema.nullable(),
      ledgerLastMovementUpdatedAt: timestampSchema.nullable(),
      expectedLedgerBalanceMinor: z.number().int().nullable(),
      differenceMinor: z.number().int().nullable(),
      cashSessionId: z.uuid().nullable(),
      lastMovementId: z.uuid().nullable(),
      sourceSnapshotId: z.uuid().nullable(),
      sourceBaselineId: z.uuid().nullable(),
      payloadHash: z.string().trim().min(1).max(128),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
    })
    .superRefine((value, context) => {
      if (value.inventoryStatus === 'unavailable') {
        if (value.rows !== null) {
          context.addIssue({
            code: 'custom',
            message: 'Unavailable inventory checkpoints must not include denomination rows.',
            path: ['rows'],
          });
        }
        if (value.totalMinor !== null) {
          context.addIssue({
            code: 'custom',
            message: 'Unavailable inventory checkpoints must not include a denomination total.',
            path: ['totalMinor'],
          });
        }
        return;
      }

      if (!value.rows) {
        context.addIssue({
          code: 'custom',
          message: 'Known inventory checkpoints require denomination rows.',
          path: ['rows'],
        });
        return;
      }
      if (value.totalMinor === null) {
        context.addIssue({
          code: 'custom',
          message: 'Known inventory checkpoints require a denomination total.',
          path: ['totalMinor'],
        });
        return;
      }

      const rowsTotal = value.rows.reduce((total, row) => total + row.denom * row.count, 0);
      if (rowsTotal !== value.totalMinor) {
        context.addIssue({
          code: 'custom',
          message: 'Known inventory checkpoint rows must match the denomination total.',
          path: ['totalMinor'],
        });
      }
    });

export const CashDenominationInventoryReadDtoSchema: z.ZodType<CashDenominationInventoryReadDto> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    cashLocationId: z.uuid(),
    cashLocationType: CashLocationTypeSchema,
    status: CashDenominationInventoryCheckpointReadStatusSchema,
    statusReason: CashDenominationInventoryCheckpointStatusReasonSchema,
    currentLedgerBalanceMinor: z.number().int(),
    currentLedgerMovementCount: z.number().int().nonnegative().nullable(),
    currentLedgerLastMovementAt: timestampSchema.nullable(),
    currentLedgerLastMovementUpdatedAt: timestampSchema.nullable(),
    latestCheckpoint: CashDenominationInventoryCheckpointDtoSchema.nullable(),
    lastKnownCheckpoint: CashDenominationInventoryCheckpointDtoSchema.nullable(),
  })
  .superRefine((value, context) => {
    if (value.status === 'pendingSync') {
      context.addIssue({
        code: 'custom',
        message: 'Slice 3A responses must not emit pendingSync.',
        path: ['status'],
      });
    }

    if (value.status === 'current' && value.latestCheckpoint?.inventoryStatus !== 'known') {
      context.addIssue({
        code: 'custom',
        message: 'Current inventory responses require a known latest checkpoint.',
        path: ['latestCheckpoint'],
      });
    }
  });

export const GetCashDenominationInventoryCheckpointRequestSchema: z.ZodType<GetCashDenominationInventoryCheckpointRequest> =
  z.object({
    companyId: z.uuid(),
    cashLocationId: z.uuid(),
  });

export const GetCashDenominationInventoryCheckpointResponseSchema: z.ZodType<GetCashDenominationInventoryCheckpointResponse> =
  z.object({
    inventory: CashDenominationInventoryReadDtoSchema,
  });
